<template>
  <div class="px-8 pt-4 pb-2" :class="{ isEditing }">
    <v-row>
      <v-col cols="12" md="4">
        <text-field
          label="Legal First Name"
          v-model="client.firstName"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <text-field
          label="Legal Last Name"
          v-model="client.lastName"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <text-field
          label="Previous Name(s)"
          v-model="client.previousName"
          :rules="[validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
        <date-field
          label="Birth Date"
          v-model="client.dateOfBirth"
          :date-picker-attrs="{
            min: minBirthDate,
            max: sevenDaysAgo
          }"
          :date-menu-attrs="{
            'offset-y': true,
            top: true
          }"
          year-picker
          :rules="[
            validators.required,
            validators.noFutureDate,
            validators.primaryClientMaxAge,
          ]"
          :readonly="readonly"
        />
      </v-col>
      <v-col cols="12" md="4">
        <multi-select-field
          label="Ethnic/Racial Demographic"
          v-model="client.ethinicity.ethinics"
          :items="[
              { value: 'Asian', text: 'Asian' },
              { value: 'BlackAfrican', text: 'Black/African' },
              { value: 'Caucasian', text: 'Caucasian' },
              { value: 'HispanicLatinx', text: 'Hispanic/Latinx' },
              { value: 'NativeAmerican', text: 'Native American' },
              { value: 'Pacific Islander', text: 'Pacific Islander' },  
              { value: 'OtherEthinicity', text: 'Other' }
          ]"
          :readonly="readonly"
        />
      </v-col>
      <v-col v-if="isOtherEthinicity" cols="12" md="4">
        <text-field
          label="Other Ethnicity details"
          v-model="client.ethinicity.otherEthinicity"
          :rules="[validators.required, validators.name]"
          maxlength="100"
          :readonly="readonly"
        />
      </v-col>      
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
       <text-field
                v-model="client.phoneNumber"
                class="mb-6"
                label="Phone number"
                placeholder="Phone number"
                required
                maxlength="132"
                :outlined= false
                :readonly="readonly"
              />
            </v-col>   
      <v-col cols="12" md="4">
        <text-field
                v-model="client.email"
                class="mb-6"
                label="Email"
                placeholder="Email"
                required
                maxlength="132"
                :outlined= false
                :readonly="readonly"
              />
        
      </v-col>
      <v-col cols="12" md="4">
        <select-field
          label="Marital Status"
          v-model="client.maritalStatus"
          :items="[
            { value: '', text: 'Select' },
            { value: 'SI', text: 'Single' },
            { value: 'MA', text: 'Married' },
            { value: 'WI', text: 'Widowed' },
            { value: 'SA', text: 'Separated' },
            { value: 'DI', text: 'Divorced' },
            { value: 'DP', text: 'Domestic Partner' }
          ]"
          :readonly="readonly"
        />
      </v-col>   
    </v-row>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import formatters from '@/helpers/formatters'
import validators from '@/helpers/validators'
import { findValidatableElement } from '@/helpers/form'

import TextField from '@/components/form/TextField'
import SelectField from '@/components/form/SelectField'
import AdsFormatContactField from '@/components/app/AdsFormatContactField'
import MultiSelectField from '@/components/form/MultiSelectField'
import DateField from '@/components/form/DateField'
import ReadOnlyField from '@/components/form/ReadOnlyField'
import RadioOptions from '@/components/form/RadioOptions'
import { GENDER_OPTIONS,DATE_OF_BIRTH_MINIMUM_YEAR } from '@/constants'

function dateToIsoDate(date) {
  return date.toISOString ? date.toISOString().slice(0, 10) : null
}

export default {
  name: 'ClientDetails',
  components: {
    TextField,
    SelectField,
    MultiSelectField,
    DateField,
    ReadOnlyField,
    RadioOptions,
    AdsFormatContactField
  },
  props: {
    readonly: {
      type: Boolean,
      defaiult: true
    }
  },
  data() {
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

    const startOfThisYear = new Date()
    startOfThisYear.setDate(1)
    startOfThisYear.setMonth(0)

    const endOfNextYear = new Date()
    endOfNextYear.setMonth(11, 31) // zero based months!
    endOfNextYear.setFullYear(startOfThisYear.getFullYear() + 1)

    const thisYear = new Date().getFullYear()
    const minAttendedYear = thisYear -16
    const maxAttendedLastDate = `${thisYear.toString()}-12-31`

    const {
      required,
      noPastDate,
      noFutureDate,
      noFutureMonthAndYear,
      noMonthAndYearBefore16years,
      name,
      primaryClientMaxAge
    } = validators

    return {
      MONTH_YEAR_DATE_FORMAT: 'MM-YYYY',
      minBirthDate: `${thisYear - DATE_OF_BIRTH_MINIMUM_YEAR}-01-01`,
      sevenDaysAgo: dateToIsoDate(sevenDaysAgo),
      endOfNextYear: dateToIsoDate(endOfNextYear),
      boolYesNo: formatters.boolYesNo,
      validators: {
        required,
        noPastDate,
        noFutureDate,
        noFutureMonthAndYear,
        noMonthAndYearBefore16years,
        name,
        primaryClientMaxAge,
        maxStartDate: validators.getMaxFutureYearValidator(1),
        prevSchoolEndDate: toDate =>
          validators.dateOrder(
            this.previousSchool.prevSchoolStartDate,
            toDate
          ) || "Cannot be earlier than 'Attended from' date"
      },
      genderOptions: GENDER_OPTIONS,
      minAttendedStartDate: new Date(`${minAttendedYear}-01-01`).toISOString().slice(0, 10),
      maxAttendedStartDate: new Date().toISOString().slice(0, 10),
      maxAttendedLastDate
    }
  },
  computed: {
    ...mapGetters([
      'application', 
      'isEditing', 
      'referenceData'
    ]),
    client() {
      return this.application.application.client
    },
  },
  methods: {
    isOtherEthinicity(){
      const ethinics = this.application.application.client.ethinicity.ethinics
      if (ethinics && ethinics.find(item => item === 'OtherEthinicity')) {
        return this.application.application.client.ethinicity.find(item => item === 'otherEthinicity').length > 0
      }
    },
    crossValidate(ref) {
      const refEl = this.$refs[ref]
      // our component doesn't implement vuetify's validatable interface
      // so we have to find the validatable element in its children
      const validatableElement = findValidatableElement(refEl)
      if (validatableElement) {
        validatableElement.validate(true)
      }
    }
  }
}
</script>
